/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useEffect, useRef, useState } from 'react';
import { Download, X } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

type Browser = 'edge' | 'chrome' | 'firefox' | 'safari' | 'other';
function detectBrowser(): Browser {
  const ua = navigator.userAgent;
  if (/Edg\//.test(ua)) return 'edge';
  if (/Firefox\//.test(ua)) return 'firefox';
  if (/Chrome\//.test(ua) && !/Edg\//.test(ua)) return 'chrome';
  if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) return 'safari';
  return 'other';
}
function steps(b: Browser): string[] {
  switch (b) {
    case 'edge': return ['Click the install icon (monitor with ↓) at the right of the address bar.', 'Or ⋯ menu → Apps → "Install this site as an app".'];
    case 'chrome': return ['Click the install icon (monitor with ↓) at the right of the address bar.', 'Or ⋮ menu → Cast, save, and share → "Install page as app".'];
    case 'firefox': return ['Firefox desktop can\'t install web apps — open Gyro Admin in Edge or Chrome to install it.'];
    case 'safari': return ['Click Share → "Add to Dock" (macOS) or "Add to Home Screen" (iOS).'];
    default: return ['Look for an install icon in your browser\'s address bar, or use Edge/Chrome for one-click install.'];
  }
}

/** The rail's Install app item: native one-click when the browser offers it (Chromium), else browser-aware steps, which Esc or a click outside closes. */
export function InstallButton() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(() => window.matchMedia?.('(display-mode: standalone)').matches ?? false);
  const [help, setHelp] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onPrompt = (e: Event) => { e.preventDefault(); setDeferred(e as BeforeInstallPromptEvent); };
    const onInstalled = () => { setInstalled(true); setDeferred(null); setHelp(false); };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => { window.removeEventListener('beforeinstallprompt', onPrompt); window.removeEventListener('appinstalled', onInstalled); };
  }, []);
  useEffect(() => {
    if (!help) return;
    const onDoc = (e: MouseEvent) => { if (wrap.current && !wrap.current.contains(e.target as Node)) setHelp(false); };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setHelp(false);
      wrap.current?.querySelector<HTMLButtonElement>('button.shell-util')?.focus();
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [help]);

  if (installed) return null;

  const click = async () => {
    if (deferred) { await deferred.prompt(); await deferred.userChoice.catch(() => undefined); setDeferred(null); return; }
    setHelp((s) => !s);
  };

  return (
    <div className="shell-install" ref={wrap}>
      <button type="button" className="shell-util" onClick={() => void click()} title="Install Gyro Admin as a desktop app" aria-expanded={deferred ? undefined : help}>
        <Download size={14} strokeWidth={1.75} aria-hidden="true" />
        <span>Install app</span>
      </button>
      {help && (
        <div className="ui-pop shell-install-help" role="dialog" aria-label="Install Gyro Admin">
          <div className="shell-install-head">
            <strong>Install Gyro Admin</strong>
            <button type="button" className="ui-btn ui-btn--ghost ui-btn--sm ui-btn--icon" aria-label="Close" onClick={() => setHelp(false)}><X size={14} strokeWidth={1.75} aria-hidden="true" /></button>
          </div>
          <ol className="shell-install-steps">
            {steps(detectBrowser()).map((s, i) => <li key={i}>{s}</li>)}
          </ol>
        </div>
      )}
    </div>
  );
}
